import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import { TPOGEN_EVENT_DELETED } from 'utils/mixpanelEventNames';
import { useEditWriteGuard } from '../../EditSession/useEditWriteGuard';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../utils/trackTpoGenEvent';

const deleteEvent = async (eventId: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.deleteEvent(eventId), {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
};

const useDeleteEventMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: (eventId: string) => {
            assertCanWrite();
            return deleteEvent(eventId);
        },
        onSuccess: (_data, eventId) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.all });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            sendNotification('success', 'Event deleted successfully');
            // TODO(PM clarification): `delete_type` is hardcoded to the CSV's
            // fe_constant 'soft'. Confirm there's no second delete path (e.g. a hard/
            // permanent delete) that would need this to vary, similar to how
            // `creation_method` on tpogen_plan_created needed two real values.
            trackTpoGenEvent(TPOGEN_EVENT_DELETED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                event_id: eventId,
                delete_type: 'soft',
            });
            onSuccess?.();
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to delete event';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useDeleteEventMutation;
